import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
    Box, 
    Button, 
    TextField, 
    Typography,
    Paper,
    IconButton,
    Popover,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    FileDownload as FileDownloadIcon,
    CalendarMonth as CalendarIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import { getQuickbooksReport, exportQuickbooksReport } from '../../../services/reportingService';

const DateInput = ({ label, value, onChange, onCalendarClick }) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
            label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            sx={{ width: 140 }}
            InputProps={{
                readOnly: true,
                endAdornment: (
                    <IconButton 
                        size="small" 
                        onClick={onCalendarClick}
                        sx={{ mr: -1 }}
                    >
                        <CalendarIcon fontSize="small" />
                    </IconButton>
                )
            }}
        />
    </Box>
);

const QuickbooksReport = ({ onBack }) => {
    // Calendar state
    const [anchorEl, setAnchorEl] = useState(null);
    const [calendarType, setCalendarType] = useState(null); // 'start' or 'end'
    // Date state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // Data state
    const [results, setResults] = useState([]);
    
    // Error state
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useAuth(); // Keep the hook but don't destructure unused variables

    const handleSearch = async () => {
        try {
            setError('');
            setSuccess('');
            
            // Basic date validation
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                    return;
            }

            // Parse dates from MM/DD/YYYY format
            const [startMonth, startDay, startYear] = startDate.split('/');
            const [endMonth, endDay, endYear] = endDate.split('/');

            // Format dates as YYYY-MM-DD for API
            const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
            const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;

            console.log('Quickbooks search parameters:', {
                startDate: formattedStartDate,
                endDate: formattedEndDate
            });
            
            const data = await getQuickbooksReport(formattedStartDate, formattedEndDate);
            console.log('Quickbooks search results:', data);
            setResults(data);
        } catch (err) {
            console.error('Search error:', err);
            setError(err.message || 'Failed to retrieve Quickbooks data');
        }
    };

    const handleExport = async () => {
        try {
            setError('');
            setSuccess('');
            
            // Basic date validation
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                return;
            }

            // Parse dates from MM/DD/YYYY format
            const [startMonth, startDay, startYear] = startDate.split('/');
            const [endMonth, endDay, endYear] = endDate.split('/');

            // Format dates as YYYY-MM-DD for API
            const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
            const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;

            console.log('Quickbooks export parameters:', {
                startDate: formattedStartDate,
                endDate: formattedEndDate
            });
            
            await exportQuickbooksReport(formattedStartDate, formattedEndDate);
            setSuccess('Quickbooks IIF file exported successfully');
        } catch (err) {
            console.error('Export error:', err);
            setError(err.message || 'Failed to export Quickbooks data');
        }
    };

    const handleCalendarClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setCalendarType(type);
    };

    const handleCalendarClose = () => {
        setAnchorEl(null);
        setCalendarType(null);
    };

    const handleDateSelect = (date) => {
        const selectedDate = dayjs(date);
        const formattedDate = selectedDate.format('MM/DD/YYYY');
        
        if (calendarType === 'start') {
            setStartDate(formattedDate);
        } else {
            setEndDate(formattedDate);
        }
        handleCalendarClose();
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 3,
                '@media (min-width:601px) and (max-width:1199px)': {
                    p: 2, // Moderate padding for tablets
                    paddingTop: 1, // Reduced top padding for tablets
                    paddingBottom: 2, // Normal bottom padding for tablets
                },
                '@media (max-width:600px)': {
                    p: 1, // Much more reduced padding for mobile
                    paddingTop: 0, // No top padding - layout provides spacing
                    paddingBottom: '88px', // Account for bottom navigation
                },
                '@media (max-width:600px) and (orientation: landscape)': {
                    p: 0.5, // Minimal padding in landscape mode
                    paddingTop: 0, // No top padding
                    paddingBottom: 0.5, // Minimal bottom padding
                    height: 'auto', // Allow natural height
                    overflow: 'visible', // Allow content to overflow
                    minHeight: 'auto', // Remove height constraints
                }
            }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    '@media (max-width:600px)': {
                        mb: 1, // Much more reduced margin for mobile
                    },
                    '@media (max-width:900px) and (orientation: landscape)': {
                        mb: 0.5, // Reduced margin in landscape mode but keep header visible
                    }
                }}>
                    <IconButton onClick={onBack} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">Quickbooks Report</Typography>
                </Box>

                {/* Controls */}
                <Paper sx={{ 
                    p: 2, 
                    mb: 3,
                    '@media (max-width:600px)': {
                        p: 0, // Remove all padding on mobile
                        mb: 1.5,
                    }
                }}>
                    {/* Desktop Layout */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        '@media (max-width:1199px)': {
                            display: 'none', // Hide desktop layout on tablets and mobile
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Start Date */}
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            />

                            {/* End Date */}
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            />

                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                <Button 
                                    variant="contained" 
                                    startIcon={<SearchIcon />}
                                    onClick={handleSearch}
                                    disabled={!startDate || !endDate}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setError('');
                                        setSuccess('');
                                        setResults([]);
                                    }}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Box>

                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={results.length === 0}
                        >
                            Export IIF
                        </Button>
                    </Box>

                    {/* Tablet Portrait Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (min-width:601px) and (max-width:1199px) and (orientation: portrait)': {
                            display: 'block',
                            px: 2, // Add horizontal padding
                            py: 2, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 2,
                            justifyContent: 'center', // Center the date fields
                            alignItems: 'center',
                        }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            />
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            />
                        </Box>

                        {/* Buttons Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2,
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                                <Button 
                                    variant="contained" 
                                    startIcon={<SearchIcon />}
                                    onClick={handleSearch}
                                    disabled={!startDate || !endDate}
                                    size="medium"
                                    sx={{ flex: 1 }}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setError('');
                                        setSuccess('');
                                        setResults([]);
                                    }}
                                    size="medium"
                                    sx={{ flex: 1 }}
                                >
                                    Clear
                                </Button>
                            </Box>
                            
                            {/* Export Button */}
                            <Button
                                variant="outlined"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExport}
                                disabled={results.length === 0}
                                size="medium"
                                sx={{ minWidth: 140 }}
                            >
                                Export IIF
                            </Button>
                        </Box>
                    </Box>

                    {/* Tablet Landscape Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (min-width:601px) and (max-width:1199px) and (orientation: landscape)': {
                            display: 'block',
                            px: 2, // Add horizontal padding
                            py: 1.5, // Add vertical padding
                        }
                    }}>
                        {/* Single Row Layout */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            {/* Date Fields */}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <DateInput 
                                    label="Start Date" 
                                    value={startDate} 
                                    onChange={setStartDate}
                                    onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                />
                                <DateInput 
                                    label="End Date" 
                                    value={endDate} 
                                    onChange={setEndDate}
                                    onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                />
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button 
                                    variant="contained" 
                                    startIcon={<SearchIcon />}
                                    onClick={handleSearch}
                                    disabled={!startDate || !endDate}
                                    size="small"
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setError('');
                                        setSuccess('');
                                        setResults([]);
                                    }}
                                    size="small"
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={handleExport}
                                    disabled={results.length === 0}
                                    size="small"
                                >
                                    Export IIF
                                </Button>
                            </Box>
                        </Box>
                    </Box>

                    {/* Mobile Portrait Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (max-width:600px) and (orientation: portrait)': {
                            display: 'block',
                            px: 1, // Add horizontal padding
                            py: 1.5, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 2,
                            justifyContent: 'center', // Center the date fields
                            alignItems: 'center',
                        }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            />
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            />
                        </Box>

                        {/* Buttons Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                                <Button 
                                    variant="contained" 
                                    startIcon={<SearchIcon />}
                                    onClick={handleSearch}
                                    disabled={!startDate || !endDate}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setError('');
                                        setSuccess('');
                                        setResults([]);
                                    }}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Clear
                                </Button>
                            </Box>
                            
                            {/* Export Button - Icon Only for Mobile */}
                            <Button
                                variant="outlined"
                                onClick={handleExport}
                                disabled={results.length === 0}
                                size="small"
                                sx={{
                                    minWidth: 'auto',
                                    padding: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1rem',
                                    }
                                }}
                            >
                                <FileDownloadIcon />
                            </Button>
                        </Box>
                    </Box>

                    {/* Mobile Landscape Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (max-width:600px) and (orientation: landscape)': {
                            display: 'block',
                            px: 1, // Add horizontal padding
                            py: 1, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 1.5,
                            justifyContent: 'center', // Center the date fields
                            alignItems: 'center',
                        }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            />
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            />
                        </Box>

                        {/* Buttons Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                                <Button 
                                    variant="contained" 
                                    startIcon={<SearchIcon />}
                                    onClick={handleSearch}
                                    disabled={!startDate || !endDate}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setError('');
                                        setSuccess('');
                                        setResults([]);
                                    }}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Clear
                                </Button>
                            </Box>
                            
                            {/* Export Button - Icon Only for Mobile Landscape */}
                            <Button
                                variant="outlined"
                                onClick={handleExport}
                                disabled={results.length === 0}
                                size="small"
                                sx={{
                                    minWidth: 'auto',
                                    padding: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1rem',
                                    }
                                }}
                            >
                                <FileDownloadIcon />
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {results.length > 0 && (
                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            mt: 3,
                            overflow: 'auto',
                            '@media (max-width:900px)': {
                                mt: 1.5, // Reduced margin for mobile
                            }
                        }}
                    >
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                                <TableRow>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>TYPE</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>DATE</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>JOB</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>EMP</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>ITEM</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>PITEM</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>DURATION</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>PROJ</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>NOTE</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>XFERTOPAYROLL</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>BILLINGSTATUS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.type}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.date}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.job}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.emp}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.item}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.pitem}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.duration}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.proj}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.note}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.xfertopayroll}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}>{row.billingstatus}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleCalendarClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <DateCalendar 
                        onChange={handleDateSelect}
                        showDaysOutsideCurrentMonth
                        firstDayOfWeek={0} // 0 = Sunday
                    />
                </Popover>
            </Box>
        </LocalizationProvider>
    );
};

export default QuickbooksReport;
